// Client-only. Recovers from a stale-chunk load error after a deploy: Vite fires
// `vite:preloadError` on the window when a lazy route chunk fails to import (the
// hashed asset the running build points at was replaced by a newer deploy — a
// 404). We turn that into ONE guarded full reload, which re-fetches the current
// HTML + current chunks, so an in-app navigation across a deploy boundary heals
// itself instead of dead-ending on the error page.

/** sessionStorage key holding the timestamp of our last auto-reload. */
export const RELOAD_GUARD_KEY = 'kiss:stale-chunk-reload-at';

/** Don't auto-reload twice inside this window — a genuinely broken chunk would
 *  otherwise spin. After one reload, a repeat error falls through to the boundary. */
export const RELOAD_GUARD_WINDOW_MS = 10_000;

/**
 * Should we auto-reload on a preload error right now? Pure so it's unit-testable.
 * Reload if we've never reloaded, or if the last reload was longer ago than the
 * guard window. Within the window we decline, so a chunk that's actually gone
 * (not just stale) surfaces to the error boundary instead of reloading forever.
 */
export function shouldReloadOnPreloadError(now: number, lastReloadAt: number | null): boolean {
	if (lastReloadAt === null) {
		return true;
	}
	return now - lastReloadAt > RELOAD_GUARD_WINDOW_MS;
}

/** Read the last-reload timestamp from sessionStorage, tolerating a missing or
 *  malformed value (returns null so we treat it as "never reloaded"). */
function readLastReloadAt(storage: Storage): number | null {
	try {
		const raw = storage.getItem(RELOAD_GUARD_KEY);
		if (raw === null) {
			return null;
		}
		const parsed = Number(raw);
		return Number.isFinite(parsed) ? parsed : null;
	} catch {
		return null;
	}
}

/**
 * Register the global preload-error handler. Client only — never call during SSR.
 * When a lazy chunk 404s we do one guarded `location.reload()`. We only
 * `preventDefault()` when we're actually handling it (reloading); if the guard
 * declines, we let the event throw so the route error boundary can show.
 */
export function registerStaleChunkRecovery(win: Window = window): void {
	win.addEventListener('vite:preloadError', (event) => {
		const now = Date.now();
		const lastReloadAt = readLastReloadAt(win.sessionStorage);
		if (!shouldReloadOnPreloadError(now, lastReloadAt)) {
			// Recently reloaded and it still failed: let it surface (error boundary),
			// don't preventDefault, don't loop.
			return;
		}
		// We're handling it — stop Vite rethrowing (suppresses the unhandled noise).
		event.preventDefault();
		try {
			win.sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
		} catch {
			// Private mode / storage disabled: reload anyway (worst case, no guard).
		}
		win.location.reload();
	});
}
